import {
    computed,
    readonly,
    ref,
    toRaw,
    type ComputedRef,
    type Ref,
} from 'vue';
import type { Router } from 'vue-router';
import { defineStore } from 'pinia';
import * as t from 'io-ts';
import { UUID } from 'io-ts-types';

import type { Field, FieldsRecord } from '@/scripts/helpers/schemaConverter/converters/types/Field';
import type { Form } from '@/scripts/helpers/schemaConverter/converters/types/Form';
import { RawFormC } from '@/scripts/helpers/schemaConverter/converters/types/Form';
import { FormFieldC } from '@/scripts/helpers/schemaConverter/converters/types/FormField';
import fromEnum from '@/scripts/io-ts/fromEnum';
import optional from '@/scripts/io-ts/optional';
import { Variant } from '@/vue/components/Elements/Button/Variant';
import { FileFieldC } from '@/scripts/helpers/schemaConverter/converters/functions/toFileUpload';

const StatusC = t.type({
    message: t.literal('success'),
    id: t.union([t.number, UUID, t.undefined]),
    title: optional(t.string),
});

export const MessageResponseC = t.type({
    title: t.string,
    message: t.string,
});

export const ResponseC = t.union([StatusC, RawFormC, MessageResponseC]);
export type Response = t.TypeOf<typeof ResponseC>;

export const ActionButtonC = t.type({
    title: t.string,
    variant: fromEnum('ButtonVariant', Variant),
});

export type ActionButton = t.TypeOf<typeof ActionButtonC>;

export type Sidebar = {
    name?: string;
    title: string;
    content: Form;
    action: string;
    preAction?: string;
    update?: string;
    button?: ActionButton;
    validatable: boolean,
    pinnable?: boolean,
    saveResponseType?: t.Type<unknown, unknown, unknown>;
};

export type Event = {
    type: EventType;
    /** @deprecated make use composition instead */
    target: {
        $router: Router,
    };
    handled?: boolean;
}

export type ResponseEvent = Event & { response: any };

export enum EventType {
    SAVE = 'save',
    CANCEL = 'cancel',
    ERROR = 'error',
}

export type Listener = {
    type: EventType;
    callable: (event: Event) => void;
}

type SidebarStoreDefinition = {
    actionResponseType: Readonly<Ref<t.Type<unknown, unknown, unknown>|null>>,
    hasSidebar: Readonly<ComputedRef<boolean>>,
    entityId: Readonly<Ref<string|number|null>>,
    entityType: Readonly<Ref<string|null>>,
    isSaving: Readonly<Ref<boolean>>,
    isPinned: Readonly<Ref<boolean>>,
    sidebar: Readonly<ComputedRef<Readonly<Sidebar>|null>>,
    dispatch: (event: Event) => void,
    createSidebar: (sidebar: Sidebar) => void,
    setContent: (content: Form) => void,
    setAction: (action: string) => void,
    setEntityId: (entityId: number|string|null) => void,
    setEntityType: (type: string|null) => void,
    setIsSaving: (value: boolean) => void,
    setIsPinned: (value: boolean) => void,
    preserveNextRouteChange: () => void,
    consumeRouteChangePreservation: () => boolean,
    on: (listener: Listener) => number,
    off: (listener: Listener) => void,
    clearSidebar: () => void,
};

const normalizeFileField = (field: Field|undefined): Field|undefined => {
    if (!field || !('file' in field)) {
        return field;
    }

    return {
        ...field,
        file: toRaw(field.file),
    };
};

const savePreviousFiles = ({
    prevFields,
    newFields,
}: {
    prevFields: FieldsRecord;
    newFields: FieldsRecord;
}) => {
    Object.entries(newFields).forEach(([key, newField]) => {
        const prevField = toRaw(prevFields[key]);
        const rawNewField = toRaw(newField);
        const prevFieldForValidation = normalizeFileField(prevField);
        const newFieldForValidation = normalizeFileField(rawNewField);

        if (
            prevField?.renderAs === 'multi-files'
            && rawNewField?.renderAs === 'multi-files'
        ) {
            if (Array.isArray(prevField.uploadFiles)) {
                // Drop pending files that the server response now lists as
                // already uploaded — otherwise a request that persisted the
                // files but failed validation would display them twice (once
                // from the server-side `files` list, once from the preserved
                // `uploadFiles`).
                const newFiles = (rawNewField as unknown as { files?: { name?: string }[] }).files;
                const persistedNames = new Set(
                    (Array.isArray(newFiles) ? newFiles : [])
                        .map((file) => file?.name)
                        .filter((name): name is string => typeof name === 'string'),
                );
                rawNewField.uploadFiles = prevField.uploadFiles
                    .filter((file: File) => !persistedNames.has(file.name));
            }

            if (Array.isArray(prevField.removedFiles)) {
                rawNewField.removedFiles = [...prevField.removedFiles];
            }
        }

        if (
            FileFieldC.is(newFieldForValidation) && FileFieldC.is(prevFieldForValidation)
            && 'file' in prevFieldForValidation
            && 'file' in rawNewField
            && prevFieldForValidation.file
        ) {
            rawNewField.file = prevFieldForValidation.file;
        }

        if (FormFieldC.is(prevField) && FormFieldC.is(rawNewField)) {
            savePreviousFiles({
                prevFields: prevField.fields as FieldsRecord,
                newFields: rawNewField.fields as FieldsRecord,
            });
        }
    });
};

export const useSidebarStore = defineStore('sidebar', (): SidebarStoreDefinition => {
    const sidebar = ref<Sidebar|null>(null);
    const entityId = ref<string|number|null>(null);
    const entityType = ref<string|null>(null);
    const listeners = ref<Listener[]>([]);
    const actionResponseType = ref<t.Type<unknown, unknown, unknown>|null>(null);
    const preserveOnNextRouteChange = ref(false);
    const isSaving = ref(false);
    const pinRequested = ref(false);

    const hasSidebar = computed<boolean>(() => sidebar.value !== null);
    const currentSidebar = computed<Readonly<Sidebar>|null>(() => sidebar.value);
    const isPinned = computed<boolean>(
        () => pinRequested.value && sidebar.value?.pinnable === true,
    );

    const dispatch = (event: Event): void => {
        listeners.value
            .filter(({ type }) => event.type === type)
            .forEach(({ callable }) => callable(event));
    };

    const createSidebar = (value: Sidebar): void => {
        sidebar.value = value;
    };

    const setContent = (content: Form): void => {
        if (!sidebar.value) {
            throw new Error('Unable to update the content when no sidebar');
        }

        const prevContent = sidebar.value.content;

        savePreviousFiles({
            prevFields: prevContent.fields,
            newFields: content.fields,
        });

        sidebar.value.content = content;
    };

    const setAction = (action: string): void => {
        if (!sidebar.value) {
            return;
        }

        sidebar.value.action = action;
    };

    const setEntityId = (value: number|string|null): void => {
        entityId.value = value;
    };

    const setEntityType = (value: string|null): void => {
        entityType.value = value;
    };

    const setIsSaving = (value: boolean): void => {
        isSaving.value = value;
    };

    const setIsPinned = (value: boolean): void => {
        pinRequested.value = value;
    };

    const preserveNextRouteChange = (): void => {
        preserveOnNextRouteChange.value = true;
    };

    const consumeRouteChangePreservation = (): boolean => {
        const shouldPreserve = preserveOnNextRouteChange.value;
        preserveOnNextRouteChange.value = false;

        return shouldPreserve;
    };

    const on = (listener: Listener): number => listeners.value.push(listener);

    const off = (listener: Listener): void => {
        listeners.value = listeners.value.filter(({ type, callable }) => {
            return type !== listener.type || callable !== listener.callable;
        });
    };

    const clearSidebar = (): void => {
        entityId.value = null;
        sidebar.value = null;
        listeners.value = [];
        preserveOnNextRouteChange.value = false;
        isSaving.value = false;
        pinRequested.value = false;
    };

    return {
        actionResponseType: readonly(actionResponseType),
        hasSidebar,
        entityId: readonly(entityId),
        entityType: readonly(entityType),
        isSaving: readonly(isSaving),
        isPinned,
        sidebar: currentSidebar,
        dispatch,
        createSidebar,
        setContent,
        setAction,
        setEntityId,
        setEntityType,
        setIsSaving,
        setIsPinned,
        preserveNextRouteChange,
        consumeRouteChangePreservation,
        on,
        off,
        clearSidebar,
    };
});

export type SidebarStore = ReturnType<typeof useSidebarStore>;
